import {
  createDataClient,
  createPublicDataClient,
} from "@/services/data-client/DataClientFactory";
import { normalizeAppointmentSettingInJson } from "../shared/fields/AppointmentSetting";

export function loadSurveyJson() {
  try {
    const savedJson = localStorage.getItem("survey-json");
    if (!savedJson) return null; // nothing saved
    const parsed = JSON.parse(savedJson);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    console.warn("Invalid survey json found in localStorage. Resetting...");
    localStorage.removeItem("survey-json"); // cleanup broken data
    return null;
  }
}

/**
 * Parse flowJson to survey JSON object
 * Handles both string and object types
 * @param {string|Object} flowJson
 * @returns {Object|null}
 */
export function parseFlowJson(flowJson) {
  if (!flowJson) return null;

  try {
    // If it's already an object, return it
    if (typeof flowJson === "object" && flowJson !== null) {
      return flowJson;
    }
    // If it's a string, parse it
    if (typeof flowJson === "string") {
      return JSON.parse(flowJson);
    }
    return null;
  } catch (e) {
    console.warn("Failed to parse flowJson:", e);
    return null;
  }
}

/**
 * Load survey from Flow API
 * @param {string} flowId - The flow ID
 * @param {string} companyKey - Company key for SmartHub adapter
 * @param {string} plan - User plan ('basic' or 'pro')
 * @returns {Promise<Object|null>} - Raw API response {} or null
 */
export async function loadSurveyFromFlow(
  flowId,
  publicApi = false,
  companyKey,
  plan = "pro"
) {
  try {
    if (!flowId) {
      console.log("No flowId provided, skipping Flow API load");
      return null;
    }

    // Create data client with appropriate plan
    const dataClient = publicApi
      ? createPublicDataClient()
      : createDataClient(plan, { companyKey });

    // Fetch the flow - returns { FlowJson, FlowId, Name, ... }
    const response = await dataClient.flows.getFlow(
      { flowId },
      publicApi ? {} : { companyKey }
    );

    if (!response) {
      console.log("No flow found with ID:", flowId);
      return null;
    }

    // Return the raw API response structure
    // Caller should check response.status === 'success' and access response.data.FlowJson
    return response;
  } catch (error) {
    console.error("Error loading survey from Flow API:", error);
    return null;
  }
}

/**
 * Save survey to Flow API
 * @param {Object} creator - SurveyCreator instance
 * @param {string} flowId - Existing flow ID (optional, for updates)
 * @param {string} flowName - Flow name/title
 * @param {string} companyKey - Company key for SmartHub adapter
 * @param {string} plan - User plan ('basic' or 'pro')
 * @param {boolean} isActive - Flow active status (default true)
 * @returns {Promise<Object>} - Saved flow data
 */
export async function saveSurveyToFlow(
  creator,
  flowId = null,
  flowName = null,
  companyKey = null,
  plan = "pro",
  isActive = true
) {
  try {
    // Create data client with appropriate plan
    const dataClient = createDataClient(plan, { companyKey });

    const surveyJson = normalizeAppointmentSettingInJson(
      JSON.parse(creator.text),
      creator.survey
    );
    const name = flowName || surveyJson.title || "Untitled Survey";
    const normalizedSurveyText = JSON.stringify(surveyJson);

    // Build the flow payload - flowJson stores the complete survey JSON as string
    const flowPayload = {
      flowId: flowId, // null for create, set for update
      name: name,
      description: surveyJson.description || "",
      flowJson: JSON.stringify({
        ...surveyJson,
        // Store theme and creator theme within flowJson for convenience
        _theme: creator.theme,
        _creatorTheme: creator.creatorTheme,
      }),
      isActive: isActive, // Preserve current active status
      isDeleted: false,
    };

    // Save via Flow API
    const savedFlow = await dataClient.flows.saveFlow(flowPayload, {
      companyKey,
    });

    // Also save to localStorage as cache
    window.localStorage.setItem("survey-json", normalizedSurveyText);
    window.localStorage.setItem(
      "survey-theme-json",
      JSON.stringify(creator.theme)
    );
    window.localStorage.setItem(
      "creator-theme-json",
      JSON.stringify(creator.creatorTheme)
    );

    console.log("✅ Flow saved to API:", savedFlow);
    return savedFlow;
  } catch (error) {
    console.error("Error saving flow:", error);
    throw error;
  }
}

/**
 * Enhanced save function that saves to both localStorage and Flow API
 * Maintains backward compatibility with existing callback pattern
 */
export async function saveSurvey(creator, saveNo, callback, options = {}) {
  const {
    flowId = null,
    flowName = null,
    companyKey = null,
    plan = "pro",
    saveToApi = true,
    isActive = true,
    onSaveSuccess = null,
  } = options;

  // Always save to localStorage first (as cache)
  const surveyJson = normalizeAppointmentSettingInJson(
    JSON.parse(creator.text),
    creator.survey
  );
  const normalizedSurveyText = JSON.stringify(surveyJson);
  window.localStorage.setItem("survey-json", normalizedSurveyText);
  window.localStorage.setItem(
    "survey-theme-json",
    JSON.stringify(creator.theme)
  );
  window.localStorage.setItem(
    "creator-theme-json",
    JSON.stringify(creator.creatorTheme)
  );

  // Save to Flow API if enabled
  if (saveToApi) {
    try {
      const isUpdate = !!flowId;
      const savedFlow = await saveSurveyToFlow(
        creator,
        flowId,
        flowName,
        companyKey,
        plan,
        isActive
      );
      console.log(
        isUpdate ? "✅ Flow updated:" : "✅ Flow created:",
        savedFlow?.flowId || savedFlow?.id
      );
      if (onSaveSuccess) {
        onSaveSuccess(savedFlow, isUpdate);
      }
      if (callback) callback(saveNo, true);
      return { ok: true, isUpdate, savedFlow };
    } catch (error) {
      console.error("Failed to save to Flow API:", error);
      // Call callback with failure
      if (callback) callback(saveNo, false);
      return { ok: false, error };
    }
  }

  if (callback) callback(saveNo, true);
  return { ok: true, isUpdate: !!flowId };
}

// Save JSON in cookie
export function saveSurveyToCookie(surveyJson) {
  document.cookie = `survey-json=${encodeURIComponent(
    JSON.stringify(surveyJson)
  )}; path=/; max-age=86400`; // 1 day
}

// Get JSON from cookie
export function getSurveyFromCookie() {
  const name = "survey-json=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(";");
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(name) === 0) {
      return JSON.parse(cookie.substring(name.length));
    }
  }
  return null;
}

export function loadSurveyTheme(key) {
  try {
    const savedTheme = localStorage.getItem(key);
    if (!savedTheme) return null; // nothing saved
    const parsed = JSON.parse(savedTheme);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    console.warn("Invalid survey theme found in localStorage. Resetting...");
    localStorage.removeItem(key); // cleanup broken data
    return null;
  }
}
